import {
  Bookmark,
  Bot,
  Coffee,
  EyeOff,
  Heart,
  MessageCircle,
  Music,
  Palette,
  RefreshCw,
  Settings,
  Share2,
  SlidersHorizontal,
  type LucideIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { postApi } from '@/api/post.api';
import type { PostStats } from '@/context/PostStatsContext';
import type { SiteConfig } from '@/api/site-config.api';

export interface ToolbarAction {
  id: string;
  icon: LucideIcon;
  label: string;
  color: string;
  action: () => void;
  count?: number;
  active?: boolean;
  visible?: boolean;
}

export function createMainActions(params: {
  siteConfig: SiteConfig | null;
  onOpenTheme?: () => void;
}): ToolbarAction[] {
  const { siteConfig, onOpenTheme } = params;
  if (!siteConfig) return [];

  return [
    {
      id: 'music',
      icon: Music,
      label: '音乐播放器',
      color: 'bg-purple-500 hover:bg-purple-600',
      action: () => {
        window.dispatchEvent(new Event('music-player:toggle'));
      },
      visible: siteConfig.music?.enabled === true,
    },
    {
      id: 'ai-pet',
      icon: Bot,
      label: 'AI宠物',
      color: 'bg-green-500 hover:bg-green-600',
      action: () => {
        const key = 'yuguige_live2d_enabled';
        const current = localStorage.getItem(key);
        const next = !(current === 'true' || current == null);
        localStorage.setItem(key, String(next));
        window.dispatchEvent(new Event('live2d:toggle'));
        toast.success(next ? 'AI宠物已开启' : 'AI宠物已关闭');
      },
      visible: siteConfig.live2d?.enabled === true,
    },
    {
      id: 'theme',
      icon: Palette,
      label: '主题设置',
      color: 'bg-blue-500 hover:bg-blue-600',
      action: () => {
        if (onOpenTheme) {
          onOpenTheme();
          return;
        }
        toast.info('主题设置暂不可用');
      },
    },
  ].filter((item) => item.visible !== false);
}

export function createInteractionActions(params: {
  postStats: PostStats;
  setPostStats: (stats: Partial<PostStats>) => void;
}): ToolbarAction[] {
  const { postStats, setPostStats } = params;

  return [
    {
      id: 'like',
      icon: Heart,
      label: '点赞',
      color: `${postStats.isLiked ? 'bg-red-500' : 'bg-gray-500'} hover:bg-red-600`,
      count: postStats.likeCount,
      active: postStats.isLiked,
      action: () => {
        if (!postStats.postId) return;
        const fn = postStats.isLiked ? postApi.unlikePost : postApi.likePost;
        fn(postStats.postId)
          .then((r) => {
            setPostStats({
              likeCount: Number.isFinite(r.likes)
                ? r.likes
                : postStats.likeCount + (postStats.isLiked ? -1 : 1),
              isLiked: r.isLiked,
            });
            toast.success(r.isLiked ? '点赞成功' : '已取消点赞');
          })
          .catch((err: any) => {
            const msg = err?.message || '';
            if (msg.includes('clientIp') || msg.includes('访客') || msg.includes('IP')) {
              toast.error('无法记录点赞，请检查网络后重试');
            } else {
              toast.error('点赞失败');
            }
          });
      },
    },
    {
      id: 'bookmark',
      icon: Bookmark,
      label: '收藏网页',
      color: 'bg-gray-500 hover:bg-yellow-600',
      count: 0,
      active: false,
      action: () => {
        const title = document.title || '羽归阁博客';
        const url = window.location.href;
        const win = window as any;

        try {
          if (win.external?.AddFavorite) {
            win.external.AddFavorite(url, title);
            toast.success('已调用浏览器收藏功能');
            return;
          }

          if (win.sidebar?.addPanel) {
            win.sidebar.addPanel(title, url, '');
            toast.success('已调用浏览器收藏功能');
            return;
          }
        } catch {
          // ignore and fallback to shortcut tip
        }

        toast('请按 Ctrl+D（Mac: Command+D）收藏当前页面');
      },
    },
    {
      id: 'comment',
      icon: MessageCircle,
      label: '评论',
      color: 'bg-indigo-500 hover:bg-indigo-600',
      count: postStats.commentCount,
      action: () => {
        const el = document.getElementById('comments');
        el ? el.scrollIntoView({ behavior: 'smooth' }) : toast.info('评论区未加载');
      },
    },
    {
      id: 'share',
      icon: Share2,
      label: '分享',
      color: 'bg-teal-500 hover:bg-teal-600',
      action: async () => {
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(window.location.href);
          } else {
            const textarea = document.createElement('textarea');
            textarea.value = window.location.href;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
          }
          toast.success('链接已复制到剪贴板');
        } catch {
          toast.error('复制失败，请手动复制地址栏链接');
        }
      },
    },
  ];
}

export function createUtilityActions(params: {
  onOpenEffects?: () => void;
  effectsEnabled: boolean;
  supportEnabled: boolean;
  setIsExpanded: (next: boolean) => void;
  setSupportOpen: (next: boolean) => void;
}): ToolbarAction[] {
  const {
    onOpenEffects,
    effectsEnabled,
    supportEnabled,
    setIsExpanded,
    setSupportOpen,
  } = params;

  return [
    {
      id: 'effects',
      icon: Settings,
      label: '特效设置',
      color: 'bg-gray-600 hover:bg-gray-700',
      action: () => (onOpenEffects ? onOpenEffects() : toast.info('特效设置暂不可用')),
      visible: effectsEnabled,
    },
    {
      id: 'floating-settings',
      icon: SlidersHorizontal,
      label: '悬浮按钮设置',
      color: 'bg-blue-600 hover:bg-blue-700',
      action: () => {
        setIsExpanded(false);
        window.dispatchEvent(new Event('floating-settings:open'));
      },
    },
    {
      id: 'hide-toolbar',
      icon: EyeOff,
      label: '隐藏悬浮菜单',
      color: 'bg-slate-600 hover:bg-slate-700',
      action: () => {
        setIsExpanded(false);
        window.dispatchEvent(new Event('floating-toolbar:hide'));
      },
    },
    {
      id: 'refresh',
      icon: RefreshCw,
      label: '刷新页面',
      color: 'bg-orange-500 hover:bg-orange-600',
      action: () => window.location.reload(),
    },
    {
      id: 'support',
      icon: Coffee,
      label: '支持作者',
      color: 'bg-amber-500 hover:bg-amber-600',
      action: () => {
        if (!supportEnabled) {
          toast('当前未开启支持作者');
          return;
        }
        setSupportOpen(true);
      },
      visible: supportEnabled,
    },
  ].filter((item) => item.visible !== false);
}
